import { Injectable } from '@angular/core';
import { AngularFireDatabase } from 'angularfire2/database';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/first';

/**
 * Service for loading and mapping contact data from Firebase
 * Handles the common pattern of loading cliente contacts and mapping them with full contact details
 */
@Injectable()
export class ContactLoaderService {

    constructor(private afDB: AngularFireDatabase) { }

    /**
     * Load contacts for a given client with full contact details
     * @param clienteId - Client ID
     * @param filterByHubspotId - If true, only return contacts with idHubspot
     * @returns Observable array of contacts with full details
     */
    loadClientContacts(clienteId: string, filterByHubspotId: boolean = false): Observable<any[]> {
        return this.afDB.list('clientes/' + clienteId + '/contactos')
            .snapshotChanges()
            .map(contactos => {
                let contactosArr = [];
                contactos.forEach(contacto => {
                    const $key = contacto.payload.key;
                    const data = { $key, ...contacto.payload.val() };
                    contactosArr.push(data);
                });
                return contactosArr;
            })
            .switchMap((contactosRefs: any[]) => {
                if (contactosRefs.length === 0) {
                    return Observable.of([]);
                }

                const contactObservables = contactosRefs.map(contactoRef =>
                    this.afDB.object('contactos/' + contactoRef.$key)
                        .snapshotChanges()
                        .take(1)
                        .map(contacto => {
                            const $key = contacto.payload.key;
                            const data = { $key, ...contacto.payload.val() };
                            return data;
                        })
                );

                return Observable.forkJoin(contactObservables)
                    .map((contacts: any[]) => {
                        if (filterByHubspotId) {
                            return contacts.filter(c => c.idHubspot);
                        }
                        return contacts;
                    });
            });
    }

    /**
     * Load contacts for a client and execute callback for each
     * Legacy pattern support - loads contacts one by one with callback
     * @param clienteId - Client ID
     * @param callback - Function to call for each loaded contact
     */
    loadClientContactsWithCallback(clienteId: string, callback: (contact: any) => void): void {
        this.afDB.list('clientes/' + clienteId + '/contactos')
            .snapshotChanges()
            .map(contactos => {
                let contactosArr = [];
                contactos.forEach(contacto => {
                    const $key = contacto.payload.key;
                    const data = { $key, ...contacto.payload.val() };
                    contactosArr.push(data);
                });
                return contactosArr;
            })
            .first()
            .subscribe((resultado) => {
                for (const contact in resultado) {
                    const dato = resultado[contact];
                    this.afDB.object('contactos/' + dato.$key)
                        .snapshotChanges()
                        .take(1)
                        .map(contacto => {
                            const $key = contacto.payload.key;
                            const data = { $key, ...contacto.payload.val() };
                            return data;
                        })
                        .subscribe((contactoCliente) => {
                            callback(contactoCliente);
                        });
                }
            });
    }

    /**
     * Map contact snapshot to object with $key
     * @param contactSnapshot - Firebase snapshot
     * @returns Contact object with $key
     */
    mapContactSnapshot(contactSnapshot: any): any {
        const $key = contactSnapshot.payload.key;
        const data = { $key, ...contactSnapshot.payload.val() };
        return data;
    }

    /**
     * Map array of contact references to contact objects
     * @param contactRefs - Array of contact reference objects with $key
     * @returns Observable array of full contact objects
     */
    mapContactReferences(contactRefs: any[]): Observable<any[]> {
        if (contactRefs.length === 0) {
            return Observable.of([]);
        }

        const contactObservables = contactRefs.map(ref =>
            this.afDB.object('contactos/' + ref.$key)
                .snapshotChanges()
                .take(1)
                .map(this.mapContactSnapshot)
        );

        return Observable.forkJoin(contactObservables);
    }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/helpers/contact-loader.service.ts